import { Message, Type, Field, OneOf } from 'protobufjs/light';
import { encode, trim } from 'url-safe-base64';
import { FlightClass } from "src/app/models/DealsModels";
declare const require: any;
const Long = require('long') as typeof import('long');

const BASE_URL = 'https://www.google.com/travel/flights/search';

function convertFlightClassToNumber(flightClass: FlightClass | undefined): number {
    if (!flightClass) {
        return 1; // Default to Economy
    }

    const mapping: Record<FlightClass, number> = {
        [FlightClass.Economy]: 1,
        [FlightClass.PremiumEconomy]: 2,
        [FlightClass.Business]: 3,
        [FlightClass.FirstClass]: 4,
    };

    return mapping[flightClass] || 1; // Default to Economy if unknown
}

export interface GoogleFlightUrlParams {
    flightClass?: FlightClass; // defaults to Economy

    /**
     * ISO Date format YYYY-MM-DD
     */
    startDate: string;

    /**
     * ISO Date format YYYY-MM-DD
     */
    returnDate: string;

    /**
     * 3 letter airport code of origin/departure airport
     */
    departureAirportCode: string;

    /**
     * 3 letter code for destination/arrival airport
     */
    destinationAirportCode: string;
}

export function generateGoogleFlightsUrl(
    params: GoogleFlightUrlParams
): string {
    const paramsMessage = new GoogleFlightsUrlParamsMessage({
        flightClass: convertFlightClassToNumber(params?.flightClass),
        flightLegs: [
            new FlightLeg({
                flightDate: params.startDate,
                departureAirport: new Airport({
                    airportCode: params.departureAirportCode,
                }),
                arrivalAirport: new Airport({
                    airportCode: params.destinationAirportCode,
                }),
            }),
            new FlightLeg({
                flightDate: params.returnDate,
                departureAirport: new Airport({
                    airportCode: params.destinationAirportCode,
                }),
                arrivalAirport: new Airport({
                    airportCode: params.departureAirportCode,
                }),
            }),
        ],
    });

    const bytes: Uint8Array =
        GoogleFlightsUrlParamsMessage.encode(paramsMessage).finish();

    let binary = '';
    for (let i = 0; i < bytes.length; i++) {
        binary += String.fromCharCode(bytes[i]);
    }
    const base64 = btoa(binary);

    const encodedParams = trim(encode(base64));

    return `${BASE_URL}?tfs=${encodedParams}`;
}

// export enum FlightClass {
//     ECONOMY = 1,
//     PREMIUM_ECONOMY = 2,
//     BUSINESS = 3,
//     FIRST = 4,
// }

@Type.d('Airport')
class Airport extends Message<Airport> {
    @Field.d(1, 'int32', 'required')
    public always1: number = 1;

    @Field.d(2, 'string', 'required')
    public airportCode: string;
}

@Type.d('FlightLeg')
class FlightLeg extends Message<FlightLeg> {
    @Field.d(2, 'string', 'required')
    public flightDate: string;

    @Field.d(5, 'int32', 'optional', 1)
    public maxStops: number;

    @Field.d(12, 'int32', 'optional')
    public maxFlightMinutes: number;

    @Field.d(13, Airport, 'required')
    public departureAirport: Airport;

    @Field.d(14, Airport, 'required')
    public arrivalAirport: Airport;
}

@Type.d('MagicObject')
class MagicObject extends Message<MagicObject> {
    @Field.d(1, 'uint64', 'required')
    public magicNumber = Long.MAX_UNSIGNED_VALUE;
}

@Type.d('GoogleFlightsUrlParamsMessage')
class GoogleFlightsUrlParamsMessage extends Message<GoogleFlightsUrlParamsMessage> {
    @Field.d(1, 'int32', 'required')
    public unknownField1: number = 28; // magic number

    @Field.d(2, 'int32', 'required')
    public searchState: number = 2; // 2 to execute search on load

    @Field.d(3, FlightLeg, 'repeated')
    public flightLegs: FlightLeg[];

    @Field.d(9, 'int32', 'required', 1)
    public flightClass: number;

    @Field.d(12, 'int32', 'optional')
    public maxPrice: number;

    @Field.d(14, 'int32', 'required')
    public unknownField14: number = 1; //magic number

    @Field.d(16, MagicObject, 'required')
    public magic: MagicObject = new MagicObject();

    @Field.d(19, 'int32', 'required')
    public unknownField19: number = 1; //magic number
}
